// src/pages/TenantManagement.tsx
// SuperAdmin tenant management page

import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Plus, 
  Search, 
  Users, 
  Activity, 
  AlertCircle,
  CheckCircle,
  Clock,
  Ban,
  Eye
} from 'lucide-react';
import { tenantManagementAPI } from '../services/tenantManagementAPI';
import { TenantRecord, TenantStatus, TenantStats } from '../types/tenant';
import CreateTenantModal from '../components/tenant/CreateTenantModal';
import TenantDetailsModal from '../components/tenant/TenantDetailsModal';

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantRecord | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Load tenants and stats on mount
  useEffect(() => {
    loadTenants();
    loadStats();
  }, [statusFilter]);

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tenantManagementAPI.listTenants({
        status: statusFilter,
        limit: 50
      });
      
      if (response.success && response.data) {
        setTenants(response.data.tenants);
      } else {
        throw new Error(response.error?.message || 'Failed to load tenants');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await tenantManagementAPI.getTenantStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadTenants();
      return;
    }

    setLoading(true);
    try {
      const response = await tenantManagementAPI.searchTenants(searchTerm);
      if (response.success && response.data) {
        setTenants(response.data.tenants);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setIsCreateModalOpen(true);
  };

  const handleTenantCreated = (newTenant: TenantRecord) => {
    setTenants(prev => [newTenant, ...prev]);
    setIsCreateModalOpen(false);
    loadStats(); // Refresh stats
  };

  const handleViewTenant = (tenant: TenantRecord) => {
    setSelectedTenant(tenant);
    setIsDetailsModalOpen(true);
  };

  const handleTenantUpdated = (updatedTenant: TenantRecord) => {
    setTenants(prev => 
      prev.map(t => t.tenantId === updatedTenant.tenantId ? updatedTenant : t)
    );
    setSelectedTenant(updatedTenant);
    loadStats(); // Refresh stats
  };

  const getStatusIcon = (status: TenantStatus) => {
    switch (status) {
      case 'ACTIVE': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'TRIAL': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'INACTIVE': return <Ban className="w-4 h-4 text-gray-500" />;
      case 'SUSPENDED': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'PENDING_SETUP': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: TenantStatus) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'TRIAL': return 'bg-yellow-100 text-yellow-800';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800';
      case 'SUSPENDED': return 'bg-red-100 text-red-800';
      case 'PENDING_SETUP': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading tenants...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-7 h-7 mr-3 text-blue-600" />
            Tenant Management
          </h1>
          <p className="text-gray-600 mt-1">Manage police departments and organizations</p>
        </div>
        
        <button
          onClick={handleCreateTenant}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Tenant
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Tenants</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalTenants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.activeTenants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Trial</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.trialTenants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Recent</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.recentlyCreated}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tenants by name, email, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TenantStatus | 'all')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="TRIAL">Trial</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="PENDING_SETUP">Pending Setup</option>
            </select>
            
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        </div>
      )}

      {/* Tenants List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Tenants ({tenants.length})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {tenants.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tenants found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first tenant.'}
              </p>
            </div>
          ) : (
            tenants.map((tenant) => (
              <div key={tenant.tenantId} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {tenant.organizationName}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(tenant.status)}`}>
                          {getStatusIcon(tenant.status)}
                          <span className="ml-1">{tenant.status}</span>
                        </span>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>{tenant.contactEmail || 'No email'}</span>
                        {tenant.city && tenant.state && (
                          <span>{tenant.city}, {tenant.state}</span>
                        )}
                        <span className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          Max {tenant.maxUsers || 0} users
                        </span>
                        {tenant.subscriptionTier && (
                          <span className="capitalize">{tenant.subscriptionTier.toLowerCase()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewTenant(tenant)}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {isCreateModalOpen && (
        <CreateTenantModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onTenantCreated={handleTenantCreated}
        />
      )}

      {/* Tenant Details Modal */}
      {isDetailsModalOpen && selectedTenant && (
        <TenantDetailsModal
          isOpen={isDetailsModalOpen}
          tenant={selectedTenant}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedTenant(null);
          }}
          onTenantUpdated={handleTenantUpdated}
        />
      )}
    </div>
  );
};

export default TenantManagement;
